import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';

// Layouts
import MainLayout from '@layouts/MainLayout';
import AuthLayout from '@layouts/AuthLayout';

// Pages
import HomePage from '@pages/HomePage';
import NotFoundPage from '@pages/NotFoundPage';
import LoginPage from '@pages/LoginPage';
import RegisterPage from '@pages/RegisterPage';
import AcceptInvitationPage from '@pages/AcceptInvitationPage';
import ForgotPasswordPage from '@pages/ForgotPasswordPage';
import ResetPasswordPage from '@pages/ResetPasswordPage';
import VerifyEmailPage from '@pages/VerifyEmailPage';
import ConfirmEmailChangePage from '@pages/ConfirmEmailChangePage';
import OrganizationsPage from '@pages/OrganizationsPage';
import OrganizationDetailPage from '@pages/OrganizationDetailPage';
import ContextDetailPage from '@pages/ContextDetailPage';
import ImportScanHistoryPage from '@pages/ImportScanHistoryPage';
import IssuesBreakdownReportPage from '@pages/IssuesBreakdownReportPage';
import BulkRegisterContextsPage from '@pages/BulkRegisterContextsPage';
import MigrationWizardPage from '@pages/MigrationWizardPage';
import AuditReviewPage from '@pages/AuditReviewPage';
import ManualChecklistPage from '@pages/ManualChecklistPage';
import AuditInitiationPage from '@pages/AuditInitiationPage';
import AuditBenchmarkPage from '@pages/AuditBenchmarkPage';
import CriterionDetailPage from '@pages/CriterionDetailPage';
import LegacyConsultantRedirect from '@pages/LegacyConsultantRedirect';
import OrgStatusReportPage from '@pages/OrgStatusReportPage';
import LegacyStatusReportRedirect from '@pages/LegacyStatusReportRedirect';
import RemediationPlansPage from '@pages/RemediationPlansPage';
import RemediationPlanDetailPage from '@pages/RemediationPlanDetailPage';
import OrgFindingsPage from '@pages/OrgFindingsPage';
import FindingDetailPage from '@pages/FindingDetailPage';
import IntegrationsPage from '@pages/IntegrationsPage';
import TestExecutionPage from '@pages/TestExecutionPage';
import TestRunDetailPage from '@pages/TestRunDetailPage';
import SettingsPage from '@pages/SettingsPage';
import BillingPage from '@pages/BillingPage';
import WebhooksPage from '@pages/WebhooksPage';
import TeamDetailPage from '@pages/TeamDetailPage';
import HelpPage from '@pages/HelpPage';
import TermsOfServicePage from '@pages/legal/TermsOfServicePage';
import PrivacyPolicyPage from '@pages/legal/PrivacyPolicyPage';
import DataProcessingAgreementPage from '@pages/legal/DataProcessingAgreementPage';
import SubprocessorsPage from '@pages/legal/SubprocessorsPage';

// Components
import ProtectedRoute from '@components/ProtectedRoute';
import { LegacyTokenRedirect } from './utils/singleUseToken';
import { useLocalePreference } from '@hooks/useLocalePreference';

/**
 * REV-588: throws during render, on purpose. Reachable only at /__boom in a
 * dev build (see the route below).
 */
function CrashOnPurpose(): never {
  throw new Error('Deliberate render error for the REV-588 error-boundary gate');
}

function App() {
  // REV-798: apply the signed-in person's stored language preference.
  useLocalePreference();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Auth routes - no sidebar */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* REV-521: the target of the invitation email link. Public on
              purpose — the recipient may not have an account yet.
              REV-878: the emails link to these pages with the token in the
              URL fragment (#token=…), which no server or Referer header sees;
              each page reads it, strips it from the URL and posts it. */}
          <Route path="/invites/accept" element={<AcceptInvitationPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* REV-683: the email-change confirmation link. */}
          <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
          {/* REV-878, deprecated: links sent before the fragment form put the
              token in the path. Converted to the fragment form at once, before
              any request is made. Remove with the API's path-token routes, 7
              days after the fragment-link emails reach production. */}
          <Route
            path="/invites/:token/accept"
            element={<LegacyTokenRedirect to="/invites/accept" />}
          />
          <Route
            path="/reset-password/:token"
            element={<LegacyTokenRedirect to="/reset-password" />}
          />
          <Route path="/verify-email/:token" element={<LegacyTokenRedirect to="/verify-email" />} />
        </Route>

        {/* Main app routes */}
        <Route element={<MainLayout />}>
          {/* REV-939: the dashboard is for signed-in users. It was public, so a
              signed-out first visit landed on an empty Dashboard instead of
              sign-in. ProtectedRoute decides from the persisted session and
              waits while it is being restored; an expired access token is
              renewed from the refresh cookie by the API client on the first
              request, so a returning user is not bounced to sign-in. */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />

          {/* Public routes */}
          <Route path="/help" element={<HelpPage />} />
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/dpa" element={<DataProcessingAgreementPage />} />
          <Route path="/legal/subprocessors" element={<SubprocessorsPage />} />

          {/* Protected routes */}
          {/* REV-897: the consultant pages were retired (REV-449); old links land
              on the tracked places of the org chosen in the header (REV-589). */}
          <Route
            path="/consultant/clients"
            element={
              <ProtectedRoute>
                <LegacyConsultantRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultant/contexts"
            element={
              <ProtectedRoute>
                <LegacyConsultantRedirect />
              </ProtectedRoute>
            }
          />
          {/* REV-897: moved to /orgs/:slug/reports/status; old links still work. */}
          <Route
            path="/consultant/clients/:orgId/status"
            element={
              <ProtectedRoute>
                <LegacyStatusReportRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs"
            element={
              <ProtectedRoute>
                <OrganizationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/teams/:teamSlug"
            element={
              <ProtectedRoute>
                <TeamDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/migrate"
            element={
              <ProtectedRoute>
                <MigrationWizardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/contexts/bulk"
            element={
              <ProtectedRoute>
                <BulkRegisterContextsPage />
              </ProtectedRoute>
            }
          />
          <Route
            // REV-497: optional :tab segment so a tracked place's tabs are
            // linkable and survive a reload. Declared AFTER the static
            // sub-routes below (import-history, reports, audits); React Router
            // ranks static segments above dynamic ones, so those still win.
            path="/orgs/:slug/contexts/:contextId/:tab?"
            element={
              <ProtectedRoute>
                <ContextDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/remediation"
            element={
              <ProtectedRoute>
                <RemediationPlansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/remediation/:planId"
            element={
              <ProtectedRoute>
                <RemediationPlanDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/reports/status"
            element={
              <ProtectedRoute>
                <OrgStatusReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/findings"
            element={
              <ProtectedRoute>
                <OrgFindingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/findings"
            element={
              <ProtectedRoute>
                <OrgFindingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/findings/:findingId"
            element={
              <ProtectedRoute>
                <FindingDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/integrations"
            element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests/new"
            element={
              <ProtectedRoute>
                <TestExecutionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests/:id"
            element={
              <ProtectedRoute>
                <TestRunDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/contexts/:contextId/import-history"
            element={
              <ProtectedRoute>
                <ImportScanHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/contexts/:contextId/reports/issues-breakdown"
            element={
              <ProtectedRoute>
                <IssuesBreakdownReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/contexts/:contextId/audits/new"
            element={
              <ProtectedRoute>
                <AuditInitiationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/audits/:auditId"
            element={
              <ProtectedRoute>
                <AuditReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/audits/:auditId/benchmark"
            element={
              <ProtectedRoute>
                <AuditBenchmarkPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/audits/:auditId/criteria/:scId"
            element={
              <ProtectedRoute>
                <CriterionDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/audits/:auditId/manual-checklist"
            element={
              <ProtectedRoute>
                <ManualChecklistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orgs/:slug/*"
            element={
              <ProtectedRoute>
                <OrganizationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            // REV-491: optional :tab so a Settings tab is linkable and survives
            // a reload. /settings/billing is declared separately below and
            // still wins — React Router ranks static segments above dynamic.
            path="/settings/:tab?"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            // REV-442. A static segment, so it ranks above /settings/:tab the
            // same way /settings/billing does.
            path="/settings/webhooks"
            element={
              <ProtectedRoute>
                <WebhooksPage />
              </ProtectedRoute>
            }
          />
          <Route
            // REV-491: Profile merged into Settings. Kept as a redirect rather
            // than deleted, so existing links and bookmarks still land somewhere
            // useful instead of 404ing.
            path="/profile"
            element={<Navigate to="/settings/profile" replace />}
          />

          {/* 404 */}
          {/* REV-588: a deliberate crash, so the error boundary's behaviour and
              its accessibility can be tested in a real browser. The known
              causes of blank pages are now guarded, which is exactly why this
              is needed — without a trigger the boundary's own gate would be
              vacuous, passing whether or not the boundary existed.

              `import.meta.env.DEV` is statically replaced by Vite, so this
              branch is dead-code-eliminated from a production build. A test
              asserts it is absent from the built bundle. */}
          {import.meta.env.DEV && <Route path="/__boom" element={<CrashOnPurpose />} />}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Box>
  );
}

export default App;
